class SocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private userId: number | null = null;
  private isConnecting = false;
  private tokenRefreshListenerAttached = false;

  connect(userId?: number): void {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      import.meta.env.DEV && console.log('[SocketService] Connection already in progress, skipping duplicate connect call');
      return;
    }

    // If already connected, just update userId if needed
    if (this.ws?.readyState === WebSocket.OPEN) {
      if (userId && this.userId !== userId) {
        this.userId = userId;
        // Join the new admin room
        this.send({
          type: 'join_room',
          payload: { room: `admin_${this.userId}` }
        });
      }
      return;
    }

    this.isConnecting = true;
    this.userId = userId || null;

    // Get token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('[SocketService] No token found in localStorage');
      this.isConnecting = false;
      return;
    }

    // Use WebSocket URL based on current domain (works in both dev and prod)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;

    import.meta.env.DEV && console.log('[SocketService] Connecting to WebSocket:', wsUrl);
    import.meta.env.DEV && console.log('[SocketService] Token preview:', token.substring(0, 50) + '...');

    try {
      this.ws = new WebSocket(wsUrl);

      // Listen for token refresh events (only once)
      if (!this.tokenRefreshListenerAttached) {
        this.setupTokenRefreshListener();
        this.tokenRefreshListenerAttached = true;
      }

      this.ws.onopen = () => {
        import.meta.env.DEV && console.log('[SocketService] Connected to WebSocket server');
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        // Join admin room if user ID is provided
        if (this.userId) {
          this.send({
            type: 'join_room',
            payload: { room: `admin_${this.userId}` }
          });
        }
      };

      this.ws.onclose = (event: CloseEvent) => {
        import.meta.env.DEV && console.log('[SocketService] Disconnected from WebSocket server:', event.code, event.reason);
        this.isConnecting = false;
        this.handleReconnect();
      };

      this.ws.onerror = (error: Event) => {
        console.error('[SocketService] WebSocket error:', error);
        this.isConnecting = false;
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data);
          import.meta.env.DEV && console.log('[SocketService] WebSocket message received:', message);
          this.handleMessage(message);
        } catch (error) {
          console.error('[SocketService] Failed to parse WebSocket message:', error);
        }
      };
    } catch (error) {
      console.error('[SocketService] Failed to create WebSocket connection:', error);
      this.isConnecting = false;
    }
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = false;

    // Remove token refresh listener
    window.removeEventListener('storage', this.handleStorageChange);
    this.tokenRefreshListenerAttached = false;
  }

  private setupTokenRefreshListener(): void {
    // Listen for localStorage changes (token refresh)
    window.addEventListener('storage', this.handleStorageChange);

    // Also listen for custom token refresh events
    window.addEventListener('tokenRefreshed', () => {
      console.log('Token refreshed event received, reconnecting WebSocket...');
      this.reconnect();
    });
  }

  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === 'token' && event.newValue) {
      console.log('Token updated in localStorage, reconnecting WebSocket...');
      this.reconnect();
    }
  }

  private reconnect(): void {
    // Don't disconnect if we're already connecting - this prevents race conditions
    if (this.isConnecting) {
      import.meta.env.DEV && console.log('[SocketService] Already connecting, skipping reconnect');
      return;
    }

    // Close existing connection but don't clear listeners
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Small delay to ensure cleanup and token update
    setTimeout(() => {
      import.meta.env.DEV && console.log('[SocketService] Reconnecting WebSocket after token refresh...');
      this.connect(this.userId || undefined);
    }, 500);
  }

  private handleMessage(message: any): void {
    switch (message.type) {
      case 'connected':
        import.meta.env.DEV && console.log('WebSocket connected:', message.payload);
        break;

      case 'room_joined':
        import.meta.env.DEV && console.log('Joined room:', message.payload.room);
        break;

      case 'room_left':
        console.log('Left room:', message.payload.room);
        break;

      case 'parserStatusUpdate':
        console.log('Parser status update received:', message.payload);
        window.dispatchEvent(new CustomEvent('parserStatusUpdate', { detail: message.payload }));
        break;

      case 'parser_progress':
        console.log('Parser progress:', message.payload || message);
        window.dispatchEvent(new CustomEvent('parser_progress', { detail: message.payload || message }));
        break;

      case 'user-status-changed':
        console.log('User status changed:', message.payload);
        window.dispatchEvent(new CustomEvent('userStatusChanged', { detail: message.payload }));
        break;

      case 'notification':
        console.log('New notification received:', message.payload);
        window.dispatchEvent(new CustomEvent('notificationReceived', { detail: message.payload }));
        break;

      case 'change_notification':
        console.log('Change notification received:', message.payload);
        window.dispatchEvent(new CustomEvent('changeNotificationReceived', { detail: message.payload }));
        break;

      case 'health-check-update':
        console.log('Health check update received:', message.payload);
        window.dispatchEvent(new CustomEvent('healthCheckUpdate', { detail: message.payload }));
        break;

      case 'system-metrics-update':
        console.log('System metrics update received:', message.payload);
        window.dispatchEvent(new CustomEvent('systemMetricsUpdate', { detail: message.payload }));
        break;

      case 'security-scan-update':
        console.log('Security scan update received:', message.payload);
        window.dispatchEvent(new CustomEvent('securityScanUpdate', { detail: message.payload }));
        break;

      case 'security-metrics-update':
        console.log('Security metrics update received:', message.payload);
        window.dispatchEvent(new CustomEvent('securityMetricsUpdate', { detail: message.payload }));
        break;

      // Escalation engine real-time events
      case 'escalation_new':
        console.log('New escalation received:', message.payload);
        window.dispatchEvent(new CustomEvent('escalationNew', { detail: message.payload }));
        break;

      case 'escalation_claimed':
        console.log('Escalation claimed:', message.payload);
        window.dispatchEvent(new CustomEvent('escalationClaimed', { detail: message.payload }));
        break;

      case 'escalation_rerouted':
        console.log('Escalation rerouted:', message.payload);
        window.dispatchEvent(new CustomEvent('escalationRerouted', { detail: message.payload }));
        break;

      case 'escalation_resolved':
        console.log('Escalation resolved:', message.payload);
        window.dispatchEvent(new CustomEvent('escalationResolved', { detail: message.payload }));
        break;

      case 'escalation_transferred':
      case 'escalation_department_transfer':
        console.log('Escalation transferred:', message.payload);
        window.dispatchEvent(new CustomEvent('escalationTransferred', { detail: message.payload }));
        break;

      case 'escalation_transferred_to_you':
        console.log('Escalation transferred to you:', message.payload);
        window.dispatchEvent(new CustomEvent('escalationTransferredToYou', { detail: message.payload }));
        break;

      case 'escalation_note_added':
        console.log('Escalation note added:', message.payload);
        window.dispatchEvent(new CustomEvent('escalationNoteAdded', { detail: message.payload }));
        break;

      // Live chat events
      case 'visitor_message':
        console.log('Visitor message:', message.payload);
        window.dispatchEvent(new CustomEvent('visitor_message', { detail: message.payload }));
        break;

      case 'conversation_accepted':
        console.log('Conversation accepted:', message.payload);
        window.dispatchEvent(new CustomEvent('conversation_accepted', { detail: message.payload }));
        break;

      default:
        console.log('Unknown message type:', message.type);
    }
  }

  private send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = 2000 * this.reconnectAttempts; // Exponential backoff

      console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);

      this.reconnectTimeout = setTimeout(() => {
        this.connect(this.userId || undefined);
      }, delay);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN || false;
  }
}

export const socketService = new SocketService();
